import type OlMap from "ol/Map"
import type {Ref} from "vue"
import {fromLonLat, toLonLat} from "ol/proj"
import Feature from "ol/Feature"
import Point from "ol/geom/Point"
import {Vector as VectorSource} from "ol/source"
import {Vector as VectorLayer} from "ol/layer"
import {Style} from "ol/style"
import Icon from "ol/style/Icon"
import Modify from "ol/interaction/Modify"
import {Collection} from "ol"

export const useOpenLayersPicker = (
  map: Ref<OlMap | null>,
  boundsByKey: Ref<BoundsByKey>,
  readOnly: Ref<boolean>,
  usePicker: Ref<boolean>,
  pickerCoordinates: Ref<LatLngAlt | undefined>,
) => {
  const { removeLayer } = useLayerManagement(map)

  const { updateBounds, resetBounds } = useFeatureBounds(boundsByKey)

  const modify: Ref<Modify | null> = ref(null)

  const initializePicker = () => {
    if (!map.value) {
      throw new Error("Map is not initialized")
    }

    map.value.on("click", (event) => {
      if (!usePicker.value) return
      if (readOnly.value) return

      const clickedCoordinate = toLonLat(event.coordinate)

      pickerCoordinates.value = { lat: clickedCoordinate[1]!, lng: clickedCoordinate[0]! }
    })

    renderPicker()
  }

  const renderPicker = () => {
    if (!map.value) return

    resetBounds(["picker"])
    removeLayer(OpenLayersLayer.PICKER)
    if (modify.value) {
      map.value.removeInteraction(modify.value)
      modify.value = null
    }

    if (!pickerCoordinates.value) return

    const pickerFeature = new Feature({
      geometry: new Point(fromLonLat([pickerCoordinates.value.lng, pickerCoordinates.value.lat])),
      type: OpenLayersLayer.PICKER,
    })

    modify.value = new Modify({
      features: new Collection([pickerFeature]),
    })

    modify.value.on("modifyend", () => {
      const movedCoordinates = toLonLat(pickerFeature.getGeometry()!.getCoordinates())

      pickerCoordinates.value = { lat: movedCoordinates[1]!, lng: movedCoordinates[0]! }
    })

    const pickerVectorSource = new VectorSource({
      features: [pickerFeature],
    })

    const pickerLayer = new VectorLayer({
      source: pickerVectorSource,
      style: getPickerStyle,
    })

    pickerLayer.set("name", OpenLayersLayer.PICKER)

    updateBounds("picker", pickerCoordinates.value.lat, pickerCoordinates.value.lng)
    map.value.addLayer(pickerLayer)
    map.value.addInteraction(modify.value!)

    modify.value.setActive(!readOnly.value)
  }

  const getPickerStyle = () => {
    const imageIcon = new Icon({
      src: "/marker-icon.png",
      scale: 0.75,
      anchor: [0.5, 1],
    })

    return new Style({
      image: imageIcon,
    })
  }

  watch([usePicker, pickerCoordinates], () => {
    if (!usePicker.value) return

    renderPicker()
  })

  return {
    initializePicker,
  }
}
